import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Briefcase, Users, CheckCircle, Clock } from 'lucide-react';
import { getUser } from '@/utils/auth';
import api from '@/utils/api';

const EmployerDashboardHome = () => {
  const navigate = useNavigate();
  const user = getUser();
  const isHR = user?.role === 'hr';
  const [stats, setStats] = useState({
    totalJobs: 0,
    activeJobs: 0,
    pendingJobs: 0,
    totalApplications: 0
  });
  const [companyName, setCompanyName] = useState('');

  useEffect(() => {
    fetchStats();
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const employerId = isHR ? user.employer_id : user.id;
      const response = await api.get(`/employer/company/${employerId}`);
      if (response.data) {
        setCompanyName(response.data.company_name || '');
      }
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const employerId = isHR ? user.employer_id : user.id;
      const url = isHR 
        ? `/employer/jobs/${employerId}?hr_id=${user.id}`
        : `/employer/jobs/${employerId}`;
      const response = await api.get(url);
      const jobs = response.data;
      
      // Calculate total applications
      const totalApplications = jobs.reduce((sum, job) => sum + (job.application_count || 0), 0);
      
      setStats({
        totalJobs: jobs.length,
        activeJobs: jobs.filter(j => j.status === 'approved' && j.active).length,
        pendingJobs: jobs.filter(j => j.status === 'pending').length,
        totalApplications: totalApplications
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const statCards = [
    { 
      icon: Briefcase, 
      label: 'Total Jobs', 
      value: stats.totalJobs, 
      color: 'bg-blue-500',
      link: '/employer/jobs'
    },
    { 
      icon: CheckCircle, 
      label: 'Active Jobs', 
      value: stats.activeJobs, 
      color: 'bg-green-500',
      link: '/employer/jobs'
    },
    { 
      icon: Clock, 
      label: 'Pending Approval', 
      value: stats.pendingJobs, 
      color: 'bg-orange-500',
      link: '/employer/jobs'
    },
    { 
      icon: Users, 
      label: 'Total Applications', 
      value: stats.totalApplications, 
      color: 'bg-purple-500',
      link: '/employer/jobs'
    }
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>
          {isHR ? 'HR Dashboard' : 'Employer Dashboard'}
        </h1>
        {companyName && (
          <p className="text-lg text-teal-600 font-medium mt-1">{companyName}</p>
        )}
      </div>
      
      <div className="grid md:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card 
            key={index} 
            className="p-6 border-slate-200 hover:shadow-lg transition-all cursor-pointer hover:border-teal-500/30"
            onClick={() => navigate(stat.link)}
            data-testid={`stat-card-${stat.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">{stat.label}</p>
                <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
              </div>
              <div className={`${stat.color} p-4 rounded-lg`}>
                <stat.icon className="w-8 h-8 text-white" />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold text-slate-900 mb-4" style={{fontFamily: 'Outfit'}}>Quick Actions</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <Card 
            className="p-4 border-slate-200 hover:shadow-md transition-all cursor-pointer hover:border-teal-500/30"
            onClick={() => navigate('/employer/jobs')}
          >
            <div className="flex items-center gap-3">
              <div className="bg-teal-100 p-3 rounded-lg">
                <Briefcase className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Post New Job</p>
                <p className="text-sm text-slate-500">Create a new job posting</p>
              </div>
            </div>
          </Card>
          
          <Card 
            className="p-4 border-slate-200 hover:shadow-md transition-all cursor-pointer hover:border-teal-500/30"
            onClick={() => navigate('/employer/jobs')}
          >
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-3 rounded-lg">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">View Applications</p>
                <p className="text-sm text-slate-500">Review candidate applications</p>
              </div>
            </div>
          </Card>
          
          {!isHR && (
            <Card 
              className="p-4 border-slate-200 hover:shadow-md transition-all cursor-pointer hover:border-teal-500/30"
              onClick={() => navigate('/employer/hr')}
            >
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Manage HR Team</p>
                  <p className="text-sm text-slate-500">Add or manage HR executives</p>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployerDashboardHome;
